import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/api';
import { TrialExamResult, ExamLibraryItem, TopicAnalysisItem } from '../types';
import { XCircleIcon, ChartBarIcon, ClipboardListIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon, CameraIcon } from './icons';
// Force compilation trigger for icons v3
import { VirtualOpticalForm } from './VirtualOpticalForm';
import { OMRScanner } from './OMR/OMRScanner';
import { ALL_TOPICS } from '../data/topics';

interface AddExamModalProps {
    type: 'TYT' | 'AYT' | 'YDT' | 'LGS';
    libraryExam?: ExamLibraryItem | null;
    onClose: () => void;
    onSave: (exam: TrialExamResult) => void;
}

// Global utility for subjects to avoid initialization order issues
const getSubjects = (t: string) => {
    switch (t) {
        case 'TYT': return [
            { key: 'Türkçe', max: 40 },
            { key: 'Matematik', max: 40 },
            { key: 'Tarih', max: 5 },
            { key: 'Coğrafya', max: 5 },
            { key: 'Felsefe', max: 5 },
            { key: 'Din Kültürü', max: 5 },
            { key: 'Fizik', max: 7 },
            { key: 'Kimya', max: 7 },
            { key: 'Biyoloji', max: 6 }
        ];
        case 'AYT': return [
            { key: 'Matematik', max: 40 },
            { key: 'Fizik', max: 14 },
            { key: 'Kimya', max: 13 },
            { key: 'Biyoloji', max: 13 },
            { key: 'Edebiyat', max: 24 },
            { key: 'Tarih-1', max: 10 },
            { key: 'Coğrafya-1', max: 6 },
            { key: 'Tarih-2', max: 11 },
            { key: 'Coğrafya-2', max: 11 },
            { key: 'Felsefe', max: 12 },
            { key: 'Din Kültürü', max: 6 }
        ];
        case 'YDT': return [
            { key: 'Yabancı Dil', max: 80 }
        ];
        case 'LGS': return [
            { key: 'Türkçe', max: 20 },
            { key: 'Matematik', max: 20 },
            { key: 'Fen Bilimleri', max: 20 },
            { key: 'T.C. İnkılap Tarihi', max: 10 },
            { key: 'Din Kültürü', max: 10 },
            { key: 'Yabancı Dil', max: 10 }
        ];
        default: return [];
    }
};

export const AddExamModal: React.FC<AddExamModalProps> = ({
    type,
    libraryExam,
    onClose,
    onSave
}) => {
    const [form, setForm] = useState({
        name: libraryExam?.name || '',
        date: new Date().toISOString().split('T')[0]
    });

    const [inputMethod, setInputMethod] = useState<'summary' | 'optical'>('summary');
    const [isAnswerKeyMode, setIsAnswerKeyMode] = useState(false);
    
    // Admin Exam States
    const [adminExams, setAdminExams] = useState<any[]>([]);
    const [isLoadingAdminExams, setIsLoadingAdminExams] = useState(false);
    const [showAdminExamList, setShowAdminExamList] = useState(false);
    const [selectedAdminExamId, setSelectedAdminExamId] = useState<string | null>(null);
    const [bookletType, setBookletType] = useState<'A' | 'B'>('A');
    const [showScanner, setShowScanner] = useState(false);
    const [scannedOptions, setScannedOptions] = useState<Record<string, string>>({});

    const [answerKeyQuestions, setAnswerKeyQuestions] = useState<Record<string, { topic: string, answer: string }>>({});
    const [manualTopics, setManualTopics] = useState<TopicAnalysisItem[]>([]);
    const [opticalTopics, setOpticalTopics] = useState<TopicAnalysisItem[]>([]);
    const [activeTopicSelection, setActiveTopicSelection] = useState<{ subject: string, questionNumber?: number, isOpen: boolean }>({ subject: '', isOpen: false });
    const [topicForm, setTopicForm] = useState({ topic: '', correct: 0, incorrect: 0, empty: 0 });

    useEffect(() => {
        const fetchAdminExams = async () => {
            setIsLoadingAdminExams(true);
            try {
                const response = await apiFetch('/api/admin-exams');
                if (response.ok) {
                    const data = await response.json();
                    setAdminExams(data.filter((e: any) => e.examType === type));
                }
            } catch (err) {
                console.error("Failed to fetch admin exams:", err);
            } finally {
                setIsLoadingAdminExams(false);
            }
        };

        fetchAdminExams();
    }, [type]);

    const handleSelectAdminExam = (exam: any) => {
        setSelectedAdminExamId(exam._id);
        setForm({
            name: `${exam.publisher} - ${exam.name}`,
            date: new Date(exam.date).toISOString().split('T')[0]
        });
        setInputMethod('optical');
        setShowAdminExamList(false);
    };


    // Handle Option Selection in Answer Key Mode
    const selectedAdminExam = adminExams.find(e => e._id === selectedAdminExamId);

    const subjectsForOptical = useMemo(() => {
        if (selectedAdminExam && selectedAdminExam.questions) {
            const subjectMap: Record<string, number[]> = {};
            selectedAdminExam.questions.forEach((q: any) => {
                if (!q.subject) return;
                if (!subjectMap[q.subject]) subjectMap[q.subject] = [];
                subjectMap[q.subject].push(q.questionNumber);
            });
            // If we have subjects defined, return them
            if (Object.keys(subjectMap).length > 0) {
                return Object.entries(subjectMap).map(([key, qNums]) => ({ 
                    key, 
                    max: qNums.length,
                    qNums: qNums.sort((a, b) => a - b)
                }));
            }
        }
        return getSubjects(type);
    }, [selectedAdminExam, type]);

    const handleAnswerKeyOptionSelect = (subject: string, questionNumber: number, option: string) => {
        const key = `${subject}-${questionNumber}`;
        setAnswerKeyQuestions(prev => ({
            ...prev,
            [key]: { ...prev[key], answer: option, topic: prev[key]?.topic || '' }
        }));
    };

    // Handle Topic Selection in Answer Key Mode
    const handleAnswerKeyTopicSelect = (topic: string) => {
        if (!activeTopicSelection.questionNumber) return;
        const key = `${activeTopicSelection.subject}-${activeTopicSelection.questionNumber}`;
        setAnswerKeyQuestions(prev => ({
            ...prev,
            [key]: { ...prev[key], topic, answer: prev[key]?.answer || '' }
        }));
        setActiveTopicSelection({ ...activeTopicSelection, isOpen: false });
    };


    const subjects = React.useMemo(() => getSubjects(type), [type]);
    const [scores, setScores] = useState<Record<string, { correct: number, incorrect: number, empty: number, net: number }>>({});

    const handleScoreChange = (subject: string, field: 'correct' | 'incorrect', value: number) => {
        const current = scores[subject] || { correct: 0, incorrect: 0, empty: 0, net: 0 };
        const updated = { ...current, [field]: value };

        // Auto calculate empty and net
        const max = subjects.find(s => s.key === subject)?.max || 0;
        updated.empty = Math.max(0, max - (updated.correct + updated.incorrect));
        const wrongImpact = type === 'LGS' ? (1/3) : 0.25;
        updated.net = updated.correct - (updated.incorrect * wrongImpact);

        setScores({ ...scores, [subject]: updated });
    };

    const recalculateSubjectScore = (subject: string, currentTopics: TopicAnalysisItem[]) => {
        const subjectTopics = currentTopics.filter(t => t.subject === subject);
        const totalCorrect = subjectTopics.reduce((acc, t) => acc + t.correct, 0);
        const totalIncorrect = subjectTopics.reduce((acc, t) => acc + t.incorrect, 0);

        const max = subjects.find(s => s.key === subject)?.max || 0;
        const empty = Math.max(0, max - (totalCorrect + totalIncorrect));
        const wrongImpact = type === 'LGS' ? (1/3) : 0.25;
        const net = totalCorrect - (totalIncorrect * wrongImpact);

        setScores(prev => ({
            ...prev,
            [subject]: {
                correct: totalCorrect,
                incorrect: totalIncorrect,
                empty,
                net
            }
        }));
    };

    const [rawAnswers, setRawAnswers] = useState<any[]>([]);

    const handleOpticalUpdate = React.useCallback((newScores: Record<string, { correct: number; incorrect: number; empty: number; net: number }>, topicAnalysis: TopicAnalysisItem[], answers: any[]) => {
        setScores(newScores);
        setOpticalTopics(topicAnalysis);
        setRawAnswers(answers);
    }, []);

    const handleScannerResults = (results: any) => {
        const newScanned: Record<string, string> = {};
        
        // Results are organized by section name: results['Türkçe'] = [{questionNumber, answer}, ...]
        Object.entries(results).forEach(([sectionName, questions]: [string, any]) => {
            questions.forEach((q: any) => {
                if (q.answer) {
                    newScanned[`${sectionName}-${q.questionNumber}`] = q.answer;
                }
            });
        });

        setScannedOptions(newScanned);
        setShowScanner(false);
        setInputMethod('optical');
    };

    const handleSave = async () => {
        if (!form.name) return;

        if (isAnswerKeyMode) {
            // Save to ExamLibrary
            const questions = Object.entries(answerKeyQuestions).map(([key, data]) => {
                const [subject, qNum] = key.split('-');
                const entry = data as { topic: string; answer: string };
                return {
                    questionNumber: parseInt(qNum),
                    subject,
                    topic: entry.topic,
                    correctAnswer: entry.answer
                };
            }).filter(q => q.correctAnswer);

            const newLibraryExam = {
                id: Date.now().toString(),
                name: form.name,
                examType: type,
                questions
            };

            try {
                const response = await apiFetch('/api/examLibrary', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newLibraryExam)
                });
                if (response.ok) {
                    onClose();
                    // Optionally refresh library exam list
                }
            } catch (err) {
                console.error("Save failed", err);
            }
            return;
        }

        if (selectedAdminExamId) {
            // Submit to AdminExam system
            const studentData = JSON.parse(localStorage.getItem('user') || '{}');
            const studentId = studentData.id || studentData._id;

            const submissionData = {
                studentId,
                examId: selectedAdminExamId,
                bookletType,
                answers: rawAnswers.map(ans => ({
                    questionNumber: ans.questionNumber,
                    answer: ans.markedOption // This matches the backend expectation
                }))
            };

            try {
                const response = await apiFetch('/api/admin-exams/submit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(submissionData)
                });
                if (response.ok) {
                    onClose();
                    // Trigger refresh in parent
                    onSave({} as any); 
                    return;
                }
            } catch (err) {
                console.error("Submit failed", err);
            }
        }

        const totalNet = (Object.values(scores) as any[]).reduce((acc, s) => acc + s.net, 0);
        const totalCorrect = (Object.values(scores) as any[]).reduce((acc, s) => acc + s.correct, 0);
        const totalIncorrect = (Object.values(scores) as any[]).reduce((acc, s) => acc + s.incorrect, 0);
        const totalEmpty = (Object.values(scores) as any[]).reduce((acc, s) => acc + s.empty, 0);

        const newExam: TrialExamResult = {
            id: Date.now(),
            name: form.name,
            date: form.date,
            examType: type,
            scores: scores,
            topicAnalysis: inputMethod === 'optical' ? opticalTopics : manualTopics,
            totalNet,
            totalCorrect,
            totalIncorrect,
            totalEmpty
        };

        onSave(newExam);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white p-8 rounded-3xl w-full max-w-5xl shadow-2xl animate-scale-in max-h-[95vh] flex flex-col">
                <div className="flex justify-between items-center mb-6 shrink-0">
                    <div>
                        <h3 className="text-2xl font-black text-gray-900 tracking-tight">
                            {isAnswerKeyMode ? `${type} Cevap Anahtarı Oluştur` : `${type} Denemesi Ekle`}
                        </h3>
                        <p className="text-gray-500 text-sm font-medium">
                            {isAnswerKeyMode
                                ? "Yeni bir deneme tanımı yaparak kütüphaneye ekle."
                                : "Sonuçlarını girerek analizini oluştur."}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        {!isAnswerKeyMode && (
                            <button
                                onClick={() => setShowAdminExamList(true)}
                                className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-black text-xs hover:bg-indigo-100 transition-all flex items-center gap-2 border border-indigo-100"
                            >
                                <PlusCircleIcon className="w-4 h-4" /> Tanımlanan Denemeler
                            </button>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors p-2" title="Kapat">
                            <XCircleIcon className="w-8 h-8" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                            <label htmlFor="exam-name" className="block text-sm font-black text-gray-700 mb-2 uppercase tracking-wider">Deneme Adı</label>
                            <input
                                id="exam-name"
                                className="w-full border-2 border-gray-100 p-3.5 rounded-2xl bg-gray-50/50 focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all"
                                placeholder="Örn: 3D Türkiye Geneli 1"
                                title="Deneme Adı"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <label htmlFor="exam-date" className="block text-sm font-black text-gray-700 mb-2 uppercase tracking-wider">Tarih</label>
                            <input
                                id="exam-date"
                                type="date"
                                className="w-full border-2 border-gray-100 p-3.5 rounded-2xl bg-gray-50/50 focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all"
                                title="Tarih Seçin"
                                value={form.date}
                                onChange={e => setForm({ ...form, date: e.target.value })}
                            />
                        </div>
                    </div>

                    {!isAnswerKeyMode && (
                        /* Input Method Toggle */
                        <div className="flex bg-gray-50 p-1.5 rounded-2xl mb-8 border border-gray-100 mobile-stack">
                            <button
                                onClick={() => setInputMethod('summary')}
                                className={`flex-1 py-3 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all ${inputMethod === 'summary'
                                    ? 'bg-white text-indigo-600 shadow-sm border border-gray-100'
                                    : 'text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                <PencilSquareIcon className="w-4 h-4" /> Özet Giriş
                            </button>
                            <button
                                onClick={() => setInputMethod('optical')}
                                className={`flex-1 py-3 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all ${inputMethod === 'optical'
                                    ? 'bg-white text-indigo-600 shadow-sm border border-gray-100'
                                    : 'text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                <ChartBarIcon className="w-4 h-4" /> Sanal Optik
                            </button>
                            <button
                                onClick={() => setShowScanner(true)}
                                className="flex-1 py-3 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all text-gray-400 hover:text-indigo-600 hover:bg-white/50"
                            >
                                <CameraIcon className="w-4 h-4" /> Kamera ile Tara
                            </button>
                        </div>
                    )}

                    {selectedAdminExamId && (
                        <div className="bg-indigo-50 p-6 rounded-[32px] border border-indigo-100 mb-8 flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center font-black">
                                    {bookletType}
                                </div>
                                <div>
                                    <h4 className="font-black text-indigo-900 text-sm">Kitapçık Türü Seçimi</h4>
                                    <p className="text-xs text-indigo-600 font-bold">Tanımlanan denemeye göre doğru sonucu hesaplamak için seçin.</p>
                                </div>
                            </div>
                            <div className="flex bg-white p-1 rounded-2xl border border-indigo-200 shadow-sm">
                                {['A', 'B'].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setBookletType(t as 'A' | 'B')}
                                        className={`px-8 py-2.5 rounded-xl font-black text-xs transition-all ${bookletType === t
                                            ? 'bg-indigo-600 text-white shadow-lg'
                                            : 'text-indigo-300 hover:text-indigo-600'
                                            }`}
                                    >
                                        {t} KİTAPÇIĞI
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isAnswerKeyMode || inputMethod === 'optical' ? (
                        <div className="h-[600px] border-2 border-dashed border-gray-100 rounded-[40px] p-2">
                            <VirtualOpticalForm
                                subjects={subjectsForOptical}
                                libraryExam={libraryExam}
                                adminExam={selectedAdminExam}
                                bookletType={bookletType}
                                mode={isAnswerKeyMode ? 'creating' : 'solving'}
                                initialSelectedOptions={scannedOptions}
                                onUpdate={handleOpticalUpdate}
                                onOptionSelect={handleAnswerKeyOptionSelect}
                                onTopicSelect={(subject, questionNumber) => setActiveTopicSelection({ subject, questionNumber, isOpen: true })}
                                answerKeyQuestions={answerKeyQuestions}
                                examType={type}
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <h4 className="font-black text-gray-400 uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                <ChartBarIcon className="w-5 h-5 text-indigo-600" /> Ders Bazlı Sonuçlar
                            </h4>
                            <div className="grid grid-cols-1 gap-4">
                                {subjects.map(sub => {
                                    const score = scores[sub.key] || { correct: 0, incorrect: 0, empty: sub.max, net: 0 };
                                    return (
                                        <div key={sub.key} className="flex flex-col sm:flex-row items-center justify-between p-6 bg-white rounded-3xl border border-gray-100 hover:border-indigo-100 transition-all gap-4 shadow-sm hover:shadow-md">
                                            <div className="w-full sm:w-1/3">
                                                <span className="font-black text-gray-900 block text-lg">{sub.key}</span>
                                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{sub.max} Soru Kapasitesi</span>
                                                <div className="mt-3 flex flex-wrap gap-1.5">
                                                    {manualTopics.filter(t => t.subject === sub.key).map((t, idx) => (
                                                        <span key={idx} className="inline-flex items-center bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-[10px] font-black border border-indigo-100">
                                                            {t.topic} ({t.correct}D {t.incorrect}Y)
                                                            <button onClick={() => {
                                                                const newTopics = manualTopics.filter(mt => mt !== t);
                                                                setManualTopics(newTopics);
                                                                recalculateSubjectScore(sub.key, newTopics);
                                                            }} className="ml-2 text-indigo-300 hover:text-red-500 transition-colors" title="Konu Sil">
                                                                <XCircleIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                    <button
                                                        onClick={() => setActiveTopicSelection({ subject: sub.key, isOpen: true })}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full text-[10px] font-black transition-all border border-transparent hover:border-indigo-100"
                                                    >
                                                        <PlusCircleIcon className="w-3.5 h-3.5" /> Konu Analizi Ekle
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6 w-full sm:w-2/3 justify-end">
                                                <div className="flex flex-col items-center">
                                                    <label htmlFor={`correct-${sub.key}`} className="text-[10px] font-black text-green-600 uppercase mb-2 tracking-widest text-center">Doğru</label>
                                                    <input
                                                        id={`correct-${sub.key}`}
                                                        type="number"
                                                        min="0" max={sub.max}
                                                        className="w-20 p-3 text-center font-black border-2 border-gray-50 rounded-2xl bg-gray-50 focus:border-green-400 focus:bg-white transition-all outline-none"
                                                        title="Doğru Sayısı"
                                                        placeholder="0"
                                                        value={score.correct || ''}
                                                        onChange={e => handleScoreChange(sub.key, 'correct', Number(e.target.value))}
                                                    />
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <label htmlFor={`incorrect-${sub.key}`} className="text-[10px] font-black text-rose-500 uppercase mb-2 tracking-widest text-center">Yanlış</label>
                                                    <input
                                                        id={`incorrect-${sub.key}`}
                                                        type="number"
                                                        min="0" max={sub.max}
                                                        className="w-20 p-3 text-center font-black border-2 border-gray-50 rounded-2xl bg-gray-50 focus:border-rose-400 focus:bg-white transition-all outline-none"
                                                        title="Yanlış Sayısı"
                                                        placeholder="0"
                                                        value={score.incorrect || ''}
                                                        onChange={e => handleScoreChange(sub.key, 'incorrect', Number(e.target.value))}
                                                    />
                                                </div>
                                                <div className="flex flex-col items-center min-w-[80px]">
                                                    <label className="text-[10px] font-black text-indigo-600 uppercase mb-2 tracking-widest text-center">Net Skor</label>
                                                    <span className="text-2xl font-black text-indigo-600 bg-indigo-50 px-4 py-2 rounded-2xl border border-indigo-100">{score.net.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Topic Selection Modal */}
                            {activeTopicSelection.isOpen && (
                                <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
                                    <div className="bg-white dark:bg-slate-900 rounded-[40px] w-full max-w-md p-10 shadow-2xl animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-white/10">
                                        <div className="flex justify-between items-center mb-8">
                                            <h4 className="font-black text-2xl text-gray-900 dark:text-white tracking-tight">{activeTopicSelection.subject} Konu Analizi</h4>
                                            <button onClick={() => setActiveTopicSelection({ ...activeTopicSelection, isOpen: false })} className="text-gray-400 hover:text-red-500 transition-colors" title="Kapat">
                                                <XCircleIcon className="w-8 h-8" />
                                            </button>
                                        </div>

                                        <div className="space-y-6 mb-10">
                                            <div>
                                                <label htmlFor="topic-select" className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Konu Seçimi</label>
                                                <select
                                                    id="topic-select"
                                                    className="w-full border-2 border-gray-100 dark:border-white/10 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 outline-none focus:border-indigo-500 font-bold dark:text-white transition-all appearance-none"
                                                    title="Konu Seçin"
                                                    value={topicForm.topic}
                                                    onChange={e => setTopicForm({ ...topicForm, topic: e.target.value })}
                                                >
                                                    <option value="">Seçiniz...</option>
                                                    {(ALL_TOPICS[`${type} - ${activeTopicSelection.subject}`] || []).map(t => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="flex gap-4">
                                                <div className="flex-1">
                                                    <label htmlFor="topic-correct" className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 text-center">Doğru</label>
                                                    <input
                                                        id="topic-correct"
                                                        type="number"
                                                        min="0"
                                                        className="w-full border-2 border-gray-50 dark:border-white/5 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 outline-none text-center font-black focus:border-green-400"
                                                        title="Doğru Sayısı"
                                                        placeholder="0"
                                                        value={topicForm.correct || ''}
                                                        onChange={e => setTopicForm({ ...topicForm, correct: Number(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label htmlFor="topic-incorrect" className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 text-center">Yanlış</label>
                                                    <input
                                                        id="topic-incorrect"
                                                        type="number"
                                                        min="0"
                                                        className="w-full border-2 border-gray-50 dark:border-white/5 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 outline-none text-center font-black focus:border-rose-400"
                                                        title="Yanlış Sayısı"
                                                        placeholder="0"
                                                        value={topicForm.incorrect || ''}
                                                        onChange={e => setTopicForm({ ...topicForm, incorrect: Number(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label htmlFor="topic-empty" className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 text-center">Boş</label>
                                                    <input
                                                        id="topic-empty"
                                                        type="number"
                                                        min="0"
                                                        className="w-full border-2 border-gray-50 dark:border-white/5 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 outline-none text-center font-black focus:border-gray-400"
                                                        title="Boş Sayısı"
                                                        placeholder="0"
                                                        value={topicForm.empty || ''}
                                                        onChange={e => setTopicForm({ ...topicForm, empty: Number(e.target.value) })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-4">
                                            <button onClick={() => setActiveTopicSelection({ ...activeTopicSelection, isOpen: false })} className="flex-1 py-4 bg-gray-100 dark:bg-white/5 font-black rounded-2xl text-gray-500 hover:bg-gray-200 transition-all uppercase tracking-widest text-xs">İptal</button>
                                            <button
                                                onClick={() => {
                                                    if (topicForm.topic) {
                                                        if (isAnswerKeyMode && activeTopicSelection.questionNumber) {
                                                            handleAnswerKeyTopicSelect(topicForm.topic);
                                                        } else {
                                                            const newTopics = [...manualTopics, {
                                                                subject: activeTopicSelection.subject,
                                                                topic: topicForm.topic,
                                                                correct: topicForm.correct,
                                                                incorrect: topicForm.incorrect,
                                                                empty: topicForm.empty,
                                                                net: topicForm.correct - (topicForm.incorrect * (type === 'LGS' ? (1/3) : 0.25))
                                                            }];
                                                            setManualTopics(newTopics);
                                                            recalculateSubjectScore(activeTopicSelection.subject, newTopics);
                                                        }
                                                        setTopicForm({ topic: '', correct: 0, incorrect: 0, empty: 0 });
                                                        setActiveTopicSelection({ ...activeTopicSelection, isOpen: false });
                                                    }
                                                }}
                                                className="flex-1 py-4 bg-indigo-600 font-black rounded-2xl text-white shadow-xl shadow-indigo-500/25 hover:bg-indigo-700 transition-all uppercase tracking-widest text-xs"
                                            >
                                                Analizi Ekle
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Admin Exam List Modal */}
                {showAdminExamList && (
                    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-[40px] w-full max-w-2xl p-8 shadow-2xl animate-scale-in max-h-[80vh] flex flex-col">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h4 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Tanımlanan Denemeler</h4>
                                    <p className="text-gray-500 text-sm font-medium">Kurumunuz tarafından tanımlanmış hazır cevap anahtarlı denemeler.</p>
                                </div>
                                <button onClick={() => setShowAdminExamList(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                                    <XCircleIcon className="w-8 h-8" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-2">
                                {isLoadingAdminExams ? (
                                    <div className="py-20 text-center text-gray-400 font-bold animate-pulse">Yükleniyor...</div>
                                ) : adminExams.length > 0 ? (
                                    adminExams.map(exam => (
                                        <div
                                            key={exam._id}
                                            onClick={() => handleSelectAdminExam(exam)}
                                            className="p-5 bg-gray-50 dark:bg-white/5 rounded-3xl border border-gray-100 dark:border-white/10 hover:border-indigo-500 transition-all cursor-pointer group flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm border border-gray-100 dark:border-white/10 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                                    <ClipboardListIcon className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <h5 className="font-black text-gray-900 dark:text-white">{exam.publisher}</h5>
                                                    <p className="text-sm text-gray-500 font-bold">{exam.name}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{new Date(exam.date).toLocaleDateString()}</p>
                                                <p className="text-[10px] font-black text-indigo-500 mt-1 uppercase">Sanal Optik Hazır</p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-20 text-center text-gray-400 italic font-medium">Henüz bu türde ({type}) tanımlanmış deneme bulunmuyor.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="pt-8 mt-8 border-t border-gray-100 flex gap-4 shrink-0 px-2 pb-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-4 bg-white border-2 border-gray-100 text-gray-500 rounded-2xl font-black hover:bg-gray-50 transition-all uppercase tracking-widest text-xs active:scale-95"
                    >
                        Vazgeç
                    </button>
                    <button
                        onClick={handleSave}
                        className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/25 uppercase tracking-widest text-xs active:scale-95 flex items-center justify-center gap-3"
                    >
                        {isAnswerKeyMode ? 'Kütüphaneye Kaydet' : 'Sınavı Kaydet'}
                        <ClipboardListIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* OMR Scanner Modal */}
                {showScanner && (
                    <OMRScanner 
                        onClose={() => setShowScanner(false)} 
                        onResults={handleScannerResults}
                        templateId={selectedAdminExamId ? 'yks-optik-129' : 'yks-optik-129'} // Logic can be improved to match template with examType
                    />
                )}
            </div>
        </div >
    );
};
